import type { AuthAccountInfo } from "@pique/core/modules/cloud/entities/session";
import type { SerializedAttendee } from "@pique/core/modules/properties/services/schema";
import type { FullSessionTranscriptionData } from "@pique/core/services/transcription-service";

import {
	generateAttendeeContext,
	prependAttendeeContextToPrompt,
} from "../../../modules/properties/services/attendee-id-utils";
import {
	AUGMENT_PROMPT_ACTION_ITEMS_DEFAULT,
	AUGMENT_PROMPT_BASE_DEFAULT,
	AUGMENT_PROMPT_DOCUMENT_BASE_TEMPLATE_DEFAULT,
	AUGMENT_PROMPT_EXAMPLE_NO_NOTES_DEFAULT,
	AUGMENT_PROMPT_EXAMPLE_WITH_NOTES_DEFAULT,
	AUGMENT_PROMPT_POSITIONING_RULES_DEFAULT,
	SOCIAL_FEED_PROMPT_DEFAULT,
} from "../../pique/setting-modal/atoms";
import type { PageContent } from "../../blocksuite/block-suite-editor/specs/custom/root-block";

export interface AugmentationPromptComponents {
	instructions: string;
	content: string;
}

interface PromptData {
	pageContent: PageContent;
	transcriptionData: FullSessionTranscriptionData | null;
	currentUser: AuthAccountInfo | null;
	pageAttendees?: SerializedAttendee[];
}

export const generateContentForAugmentation = (
	pageContent: PageContent,
	transcriptionData: FullSessionTranscriptionData | null,
): string => {
	const pageContentString = JSON.stringify(pageContent, null, 2);
	const transcriptText = transcriptionData?.text ?? "No transcript available.";
	return `USER'S NOTES:\n\`\`\`json\n${pageContentString}\n\`\`\`\n\nMEETING TRANSCRIPT:\n\`\`\`\n${transcriptText}\n\`\`\`\n`;
};

const generateBaseInstructions = (
	currentUser: AuthAccountInfo | null,
	pageAttendees?: SerializedAttendee[],
): string => {
	return prependAttendeeContextToPrompt(AUGMENT_PROMPT_BASE_DEFAULT, {
		currentUser,
		attendees: pageAttendees,
		includeTranscriptionNote: true,
	});
};

export const generateActionItemsPromptComponents = (
	promptData: PromptData,
): AugmentationPromptComponents => {
	const baseInstructions = generateBaseInstructions(
		promptData.currentUser,
		promptData.pageAttendees,
	);
	const instructions = `${baseInstructions}\n${AUGMENT_PROMPT_ACTION_ITEMS_DEFAULT}`;
	const content = generateContentForAugmentation(
		promptData.pageContent,
		promptData.transcriptionData,
	);
	return { instructions, content };
};

export const generateIncrementalSummaryPromptComponents = (
	previousSummary: string | null,
	newChunks: Array<{ tsMs: number; text: string; speaker?: string }>,
	pageAttendees?: SerializedAttendee[],
): AugmentationPromptComponents => {
	let header =
		"You are an AI assistant creating a condensed incremental summary of new meeting content. Your goal is to preserve ALL substantive information using the original text, speaker attributions, and timestamps as closely as possible as you summarize and retain a clean version of all the important parts.";

	// Add attendee context using centralized utility
	const attendeeContext = generateAttendeeContext({
		attendees: pageAttendees,
		includeTranscriptionNote: true,
	});
	if (attendeeContext) {
		header += `\n\n${attendeeContext}`;
	}
	const prev =
		previousSummary && previousSummary.trim()
			? `PREVIOUS SUMMARY (do not repeat):\n\n${previousSummary}\n\n`
			: "NOTE: No previous summary exists for this meeting.\n\n";
	const chunkLines = newChunks
		.map((c) => {
			const seconds = Math.floor(c.tsMs / 1000);
			const mm = Math.floor(seconds / 60)
				.toString()
				.padStart(2, "0");
			const ss = (seconds % 60).toString().padStart(2, "0");
			const ts = `${mm}:${ss}`;
			const speaker = c.speaker ? `${c.speaker}: ` : "";
			return `[${ts}] ${speaker}${c.text}`;
		})
		.join("\n");

	const instructions = [
		header,
		"",
		"TRANSCRIPTION QUALITY NOTE: The transcription may contain errors common to speech-to-text systems:",
		"- Misheard words or phrases (especially technical terms, names, numbers)",
		"- Missing punctuation or incorrect capitalization",
		"- Filler words, false starts, or incomplete sentences",
		"- Background noise artifacts or speaker confusion",
		"When you encounter unclear or potentially incorrect transcription segments, use context clues and your business knowledge to infer the most likely intended meaning. Fix any misspellings of company names, product names, or technical terms etc and consistently use the correct spelling and capitalization for the entirety of the summary you are building.",
		"",
		"CRITICAL PRIORITIES - Include these without exception:",
		"- Decisions made or changed (even tentative ones)",
		"- Action items, assignments, or commitments (who will do what by when)",
		"- Key metrics, numbers, budgets, or quantitative data mentioned",
		"- Problems, risks, blockers, or concerns raised",
		"- Strategic insights, realizations, or pivots discussed",
		"- Technical specifications, requirements, or constraints defined",
		"- External dependencies or stakeholder impacts identified",
		"- Deadlines, milestones, or timeline changes",
		"",
		"SUMMARIZATION RULES:",
		"- Preserve the original wording and phrasing as much as possible - do not paraphrase or reinterpret",
		"- Skip pleasantries, repetitions, and filler words",
		'- If previous summary exists, NEVER repeat information already captured. If no new information is discovered in the New Transcript Chunks below, just set false for "hasNew"',
		"- Maintain chronological order - do not reorganize content",
		'- SPEAKER PRESERVATION: If a transcript chunk has a speaker identifier, include it in your output. Copy the speaker value exactly as provided (e.g. email address or "Speaker"). Chunks labeled "Speaker" are words spoken by OTHER attendees, NOT by the current user.',
		"- If no speaker is provided for a chunk, omit the speaker field for that summary item",
		"- TIMESTAMP PRESERVATION: Use the exact timestamp from the input chunk",
		"",
		"OUTPUT REQUIREMENTS:",
		"- Preserve the original speaker voice, formatting, and tone - do not change how things were said",
		"- Include specific names, numbers, and dates exactly as mentioned",
		"- Do not interpret or add meaning - stay objective to the source material",
		"",
		"Return STRICT JSON with this exact schema:",
		"{",
		'  "hasNew": boolean,  // false if nothing substantive to add',
		'  "items"?: [',
		"    {",
		'      "timestamp": "MM:SS",',
		'      "text": "Concise but complete summary point",',
		'      "speaker": "speaker identifier from input (optional)"',
		"    }",
		"  ]",
		"}",
		"Do not include code fences or any text outside the JSON structure.",
	].join("\n");

	const content = `${prev}NEW TRANSCRIPT CHUNKS:\n\n${chunkLines}`;
	return { instructions, content };
};

export const generateSocialFeedPromptComponents = (
	notesContent: string,
	allowedEmails: string[],
	pageAttendees?: SerializedAttendee[],
	previouslyExtracted?: Array<{
		attendeeEmail: string;
		text: string;
		source?: string;
		timestamp?: string;
	}>,
): AugmentationPromptComponents => {
	const attendeeContext = generateAttendeeContext({
		attendees: pageAttendees,
		includeTranscriptionNote: true,
	});
	const prevBlock =
		Array.isArray(previouslyExtracted) && previouslyExtracted.length > 0
			? `Previously extracted items (DO NOT repeat these):\n${JSON.stringify(
					previouslyExtracted.map((i) => ({
						attendeeEmail: i.attendeeEmail,
						text: i.text,
					})),
					null,
					2,
				)}`
			: "NOTE: No previous social feed exists for this meeting.";
	const instructions = [
		attendeeContext,
		SOCIAL_FEED_PROMPT_DEFAULT,
		prevBlock,
		`Allowed Emails: [${allowedEmails.join(", ")}]`,
		"Return ONLY a JSON array matching the schema. No prose or code fences.",
	]
		.filter(Boolean)
		.join("\n\n");
	const content = notesContent;
	return { instructions, content };
};

export const generateFullAugmentInstructions = (
	currentUser: AuthAccountInfo | null,
	pageAttendees?: SerializedAttendee[],
	hasUserNotes?: boolean,
): string => {
	const base = generateBaseInstructions(currentUser, pageAttendees);

	if (hasUserNotes === false) {
		return [
			base,
			AUGMENT_PROMPT_DOCUMENT_BASE_TEMPLATE_DEFAULT,
			AUGMENT_PROMPT_EXAMPLE_NO_NOTES_DEFAULT,
			AUGMENT_PROMPT_POSITIONING_RULES_DEFAULT,
		].join("\n\n");
	}

	return [
		base,
		AUGMENT_PROMPT_DOCUMENT_BASE_TEMPLATE_DEFAULT,
		AUGMENT_PROMPT_EXAMPLE_WITH_NOTES_DEFAULT,
		AUGMENT_PROMPT_POSITIONING_RULES_DEFAULT,
	].join("\n\n");
};

export const generateUnifiedPlanPromptComponents = (
	promptData: PromptData,
): AugmentationPromptComponents => {
	const hasUserNotes = promptData.pageContent.fullText.trim().length > 0;

	const instructions = generateFullAugmentInstructions(
		promptData.currentUser,
		promptData.pageAttendees,
		hasUserNotes,
	);
	const duplicateGuard = `\n\nSTRICT DE-DUPLICATION RULES:\n- Do NOT insert any heading or content that repeats or paraphrases existing user notes.\n- Only insert new content that is clearly new and not a rephrasing of existing text.`;
	const content = generateContentForAugmentation(
		promptData.pageContent,
		promptData.transcriptionData,
	);
	return { instructions: instructions + duplicateGuard, content };
};
